import { useEffect, useMemo, useState } from 'react';
import { ClipboardList, Clock } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { fetchScipQuery } from '../components/api';
import { fetchPowerBIFabricSqlData, fetchPowerBISemanticData } from '../services/powerbiService';
import '../modules/webv2/webV2Module.css';

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-GB');
};

const formatScipStatus = (stageValue) => {
  if (stageValue === null || typeof stageValue === 'undefined') return 'Not started';
  const stage = Number(stageValue);
  const map = {
    0: 'Draft',
    1: 'In Progress',
    2: 'Submitted',
    3: 'Finalized',
    4: 'Locked'
  };
  return Number.isFinite(stage) && map[stage] ? map[stage] : String(stageValue);
};

const getScipStatusClass = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized.includes('final') || normalized.includes('lock')) return 'is-complete';
  if (normalized.includes('progress') || normalized.includes('submitted') || normalized.includes('draft')) return 'is-active';
  return 'is-empty';
};

const normalizeColumn = (name) => {
  const raw = String(name || '').trim();
  const bracketMatch = raw.match(/\[([^\]]+)\]\s*$/);
  return String(bracketMatch ? bracketMatch[1] : raw).trim().replace(/\s+/g, ' ').toLowerCase();
};

const findColumnKey = (columns, names) => {
  const normalizedNames = new Set(names.map((name) => String(name || '').trim().replace(/\s+/g, ' ').toLowerCase()));
  return columns.find((col) => normalizedNames.has(normalizeColumn(col))) || null;
};
const normalizeCountryMatchValue = (value) => String(value || '').trim().toLowerCase();
const getRowText = (row, columnKey) => (columnKey ? String(row?.[columnKey] || '').trim() : '');

const GAVI_JOURNEY_COLUMNS = [
  { key: 'strategicPlanning', label: 'Strategic planning', names: ['strategic planning'] },
  { key: 'governanceFinancing', label: 'Governance & Financing', names: ['governance & financing', 'governance and financing'] },
  { key: 'dataVisibilityUse', label: 'Data Visibility & Use', names: ['data visibility & use', 'data visibility and use'] },
  { key: 'fundamentalInfrastructure', label: 'Fundamental Infrastructure', names: ['fundamental infrastructure'] },
  { key: 'supplyChainOptimization', label: 'Supply Chain Optimization', names: ['supply chain optimization'] },
  { key: 'workforceDevelopment', label: 'Workforce Development', names: ['workforce development'] },
  { key: 'evmComposite', label: 'EVM Score', names: ['evm composite', 'evm copmposite', 'evm_composite'] }
];

const SNAPSHOT_TABS = [
  { key: 'nis', label: 'NIS' },
  { key: 'evma', label: 'EVMA' },
  { key: 'cip', label: 'CIP' },
  { key: 'nlwg', label: 'NLWG' }
];

const NIS_APPLICATION_COLUMNS = [
  { key: 'submissionToGavi', label: 'Submission to Gavi', match: 'submission to gavi' },
  { key: 'reprogrammingSubmission', label: 'Reprogramming submission', match: 'reprogramming submission date' },
  { key: 'applicationType', label: 'Pathway type', match: 'pathway type' },
  { key: 'period', label: 'Application preparation', match: 'application preparation date range' },
  { key: 'reprogrammingPeriod', label: 'Reprogramming window', match: 'reprogramming grant dates range' },
  { key: 'guaranteedVaccineBudget', label: 'Guaranteed vaccine budget', match: 'the guaranteed budget', format: 'currency' },
  { key: 'discretionaryVaccineBudget', label: 'Discretionary vaccine budget', match: 'the discretionary budget', format: 'currency' },
  { key: 'totalVaccineBudget', label: 'Total vaccine budget', match: 'the total vaccine budget', format: 'currency' },
  { key: 'totalCashBudget', label: 'Total cash budget 2026–2030', match: 'total cash budget for 2026 2030', format: 'currency' },
  { key: 'countryFoundations', label: 'Country foundations', match: 'country foundations total allocation', format: 'currency' },
  { key: 'coFinancingObligations', label: 'Co-financing obligations 2026–2030', match: 'expected co financing obligation', format: 'currency' }
];

const FINANCE_COUNTRY_ALIASES = {
  drc: 'democratic republic of the congo',
  'dr congo': 'democratic republic of the congo',
  'democratic republic of congo': 'democratic republic of the congo',
  'congo dem rep': 'democratic republic of the congo',
  'congo democratic republic of the': 'democratic republic of the congo',
  'congo republic': 'republic of the congo',
  'republic of congo': 'republic of the congo',
  congo: 'republic of the congo',
  'cote divoire': 'cote divoire',
  'cote d ivoire': 'cote divoire',
  'ivory coast': 'cote divoire',
  'the gambia': 'gambia',
  'viet nam': 'vietnam',
  'lao pdr': 'laos',
  'lao peoples democratic republic': 'laos',
  'united republic of tanzania': 'tanzania',
  'tanzania united republic of': 'tanzania',
  'syrian arab republic': 'syria',
  'bolivia plurinational state of': 'bolivia',
  'iran islamic republic of': 'iran',
  'republic of moldova': 'moldova',
  swaziland: 'eswatini',
  macedonia: 'north macedonia',
  'former yugoslav republic of macedonia': 'north macedonia',
  burma: 'myanmar',
  'cape verde': 'cabo verde',
  'east timor': 'timor leste',
  'kyrgyz republic': 'kyrgyzstan',
  'state of palestine': 'palestine',
  'occupied palestinian territory': 'palestine',
  'congo brazzaville': 'republic of the congo',
  car: 'central african republic',
  png: 'papua new guinea',
  'sao tome and principe': 'sao tome and principe'
};

const normalizeFinanceText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/&/g, ' and ')
  .replace(/['’]/g, '')
  .replace(/[^a-z0-9]+/g, ' ')
  .trim()
  .replace(/\s+/g, ' ');

const normalizeFinanceCountryName = (value) => {
  const normalized = normalizeFinanceText(value);
  return FINANCE_COUNTRY_ALIASES[normalized] || normalized;
};

const findFinanceColumnKey = (columns, match) => {
  const wanted = normalizeFinanceText(match);
  if (!wanted) return null;
  const normalizedColumns = columns.map((column) => ({
    column,
    normalized: normalizeFinanceText(column)
  }));
  return normalizedColumns.find((item) => item.normalized === wanted)?.column
    || normalizedColumns.find((item) => item.normalized.startsWith(wanted))?.column
    || null;
};

const parseNumericValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const amount = Number(String(value).replace(/,/g, '').replace(/\s/g, '').replace(/[$€£]/g, ''));
  return Number.isFinite(amount) ? amount : null;
};

const formatCurrency = (value) => {
  const amount = parseNumericValue(value);
  if (amount === null) return '';
  if (Math.abs(amount) >= 1_000_000_000) return `$${(amount / 1_000_000_000).toFixed(1)}B`;
  if (Math.abs(amount) >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (Math.abs(amount) >= 1_000) return `$${(amount / 1_000).toFixed(1)}K`;
  return `$${Math.round(amount).toLocaleString()}`;
};

const formatNisFinanceValue = (value, format) => {
  const text = String(value ?? '').replace(/\s+/g, ' ').trim();
  if (!text || text === '-' || text === '—') return '';
  if (format === 'currency') return formatCurrency(text);
  return text;
};

const EVMA_GENERIC_FIELD_KEYS = new Set([
  'LevelId',
  'Level',
  'IfEvmAssessmentDueWhatIsThePlanningStatus',
  'PlanningIsFundSecure',
  'PlanningleadCoAssessorSelected',
  'PlanningIsManagersTrained',
  'PlanningIsAssessorsTrained',
  'AssessmentIsDataCollectionCompleted',
  'AssessmentBestPracticesImplemented',
  'AssessmentIsQualityReviewEnded',
  'ReportWritingIsInitiated',
  'ReportWritingFinalized',
  'CipDevelopmentIsInitiated',
  'CipDevelopmentIsFinalized'
]);

const NLWG_GENERIC_FIELD_KEYS = new Set([
  'IsLwgEquivalentPresent',
  'AbsenceReasonForLwg',
  'IsCountryInterestedSettingUpLwg',
  'WhatSupportRequiredToSetUpLwg',
  'IsLwgFocusOnlyOnImmunization',
  'IsDefinedTorWithMembersRolesAndResponsibilities',
  'IsEndorsedOrFormallyInauguratedByMoh',
  'MeetingSchedule',
  'HeadedByDirectorOrGovernmentStaff',
  'IsManagerWithExperienceOfProgramActivities',
  'HeldAtLeastOneMeetingLastMonth',
  'CirculatedAgendaDocumentedMinutesByPartners',
  'IsMomSharedWithDecisionMakers',
  'IsConsultedRelatedToStrategiesAndPlans',
  'IsSupplyChainTeamInvolvedInAllHealthCommodities',
  'IsCountryHaveNationalImmunizationScStrategyPlan',
  'WhenCountryLastUpdateNiScPoliciesSOP',
  'IsNLWGMonitoringAndReview',
  'IsNLWGStockAvailabilityReporting',
  'IsNLWGSupplyChainDataAnalyticsCapacity',
  'IsNLWGISCEquipmentInventoryUpdate',
  'IsNLWGMonitoringShipmentsAndDistribution',
  'IsNLWGMonitoringCIPsImplementation',
  'NLWGTrainingAwareness',
  'NLWGResourcesDownloaded',
  'AdditionalNLWGResourcesNeeded'
]);

const NLWG_PRIMARY_FIELDS = [
  {
    key: 'IsLwgEquivalentPresent',
    label: 'Does your country have a National LWG/Equivalent present?'
  },
  {
    key: 'AbsenceReasonForLwg',
    label: 'What is/are the reason(s) for the absence of the National Logistics Working Group?'
  },
  {
    key: 'IsCountryInterestedSettingUpLwg',
    label: 'Does the Country have an interest in setting up an NLWG?'
  },
  {
    key: 'WhatSupportRequiredToSetUpLwg',
    label: 'What support does the country need to establish a NLWG?'
  },
  { key: 'PresenceScore', label: 'Presence Score' },
  { key: 'FunctionalityScore', label: 'Functionality Score' }
];

const formatGaviValue = (columnKey, value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  const normalized = String(value).trim().toUpperCase();
  const performanceLabels = {
    LP: 'Low Performance',
    MP: 'Medium Performance',
    HP: 'High Performance'
  };
  if (performanceLabels[normalized]) return performanceLabels[normalized];
  if (columnKey !== 'evmComposite') return String(value);

  const numericValue = Number(String(value).replace(',', '.'));
  return Number.isFinite(numericValue) ? numericValue.toFixed(2) : String(value);
};

const formatFieldLabel = (key) => String(key || '')
  .replace(/[_-]+/g, ' ')
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/\s+/g, ' ')
  .trim()
  .replace(/\b\w/g, (char) => char.toUpperCase());

const formatFieldValue = (key, value, genericDataMap = new Map()) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  if (EVMA_GENERIC_FIELD_KEYS.has(key) || NLWG_GENERIC_FIELD_KEYS.has(key)) {
    const mappedValue = genericDataMap.get(String(value).trim());
    if (mappedValue) return mappedValue;
  }
  const normalizedKey = String(key || '').toLowerCase();
  if (normalizedKey === 'stage') return formatScipStatus(value);
  if (normalizedKey.includes('date')) return formatDate(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

const buildCountryScopeClause = (allowedIso3Values) => {
  if (!Array.isArray(allowedIso3Values) || allowedIso3Values.length === 0) return '';
  const iso3List = allowedIso3Values.map((iso3) => `'${iso3.replace(/'/g, "''")}'`).join(', ');
  return `WHERE iso3 IN (${iso3List})`;
};

const ImprovementPlanningJourney = () => {
  const { user } = useUser();
  const [countries, setCountries] = useState([]);
  const [scipRows, setScipRows] = useState([]);
  const [evmProcessRows, setEvmProcessRows] = useState([]);
  const [nlwgRows, setNlwgRows] = useState([]);
  const [genericDataMasterRows, setGenericDataMasterRows] = useState([]);
  const [gaviSemanticRows, setGaviSemanticRows] = useState([]);
  const [gaviSemanticColumns, setGaviSemanticColumns] = useState([]);
  const [gaviSemanticError, setGaviSemanticError] = useState('');
  const [nisFinanceRows, setNisFinanceRows] = useState([]);
  const [nisFinanceColumns, setNisFinanceColumns] = useState([]);
  const [nisFinanceError, setNisFinanceError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedCountryKey, setSelectedCountryKey] = useState('');
  const [activeSnapshotTab, setActiveSnapshotTab] = useState('nis');
  const [showAllNlwgFields, setShowAllNlwgFields] = useState(false);
  const [showAllJourneyCountries, setShowAllJourneyCountries] = useState(false);

  const normalizedAccessLevel = String(user?.accessLevel || '').toLowerCase();
  const gaviFinanceDatasetId = process.env.REACT_APP_GAVI_FINANCE_DATASET_ID || 'e1a596ea-23a5-495e-b5f4-faaa557bbe77';
  const gaviFinanceWorkspaceId = process.env.REACT_APP_GAVI_FINANCE_WORKSPACE_ID || '7d441575-4aa9-4260-99dd-543f002c0340';
  const gaviFinanceDaxQuery = process.env.REACT_APP_GAVI_FINANCE_DAX_QUERY || '';
  const gaviFinanceTableName = process.env.REACT_APP_GAVI_FINANCE_TABLE_NAME || 'NISCostTableSharepoint';
  const gaviFinanceUpdateTableName = process.env.REACT_APP_GAVI_FINANCE_UPDATE_TABLE_NAME || 'GaviFinanceUpdate';
  const allowedIso3Values = useMemo(() => {
    const fromAllowed = Array.isArray(user?.allowedCountriesIso3) ? user.allowedCountriesIso3 : [];
    const fromCountry = user?.country_iso3 ? [user.country_iso3] : [];
    return [...fromAllowed, ...fromCountry]
      .map((value) => String(value || '').trim().toUpperCase())
      .filter(Boolean)
      .filter((value, idx, arr) => arr.indexOf(value) === idx);
  }, [user?.allowedCountriesIso3, user?.country_iso3]);

  useEffect(() => {
    let mounted = true;

    const loadJourneyData = async () => {
      setIsLoading(true);
      setError('');

      try {
        const isGlobalScope = normalizedAccessLevel === 'global' || normalizedAccessLevel === 'admin';
        const countryScopeClause = isGlobalScope ? '' : buildCountryScopeClause(allowedIso3Values).replace('WHERE iso3', 'WHERE IsoCode');
        const countriesQuery = `
          SELECT
            Id,
            Name,
            IsoCode,
            UnicefRegionCode,
            UnicefRegionName,
            WhoRegionCode,
            WhoRegionName
          FROM SCIP.CountryMaster
          ${countryScopeClause}
            ${countryScopeClause ? 'AND' : 'WHERE'} ISNULL(IsActive, 1) = 1
          ORDER BY Name;
        `;
        const countryRows = await fetchScipQuery(countriesQuery, {});
        const scipQuery = `
          SELECT
            CBI.Id,
            CBI.Name,
            CM.Name AS CountryName,
            CM.IsoCode AS ISO3,
            CBI.Stage,
            CBI.UpdatedDate
          FROM SCIP.CipBasicInformation CBI
          LEFT JOIN SCIP.CountryMaster CM ON CM.Id = CBI.CountryId
          ORDER BY CBI.UpdatedDate DESC;
        `;
        const scipData = await fetchScipQuery(scipQuery, {});
        const evmProcessQuery = `
          SELECT
            CEPT.CountryId,
            CM.Name AS CountryName,
            CM.IsoCode AS ISO3,
            CEPT.LevelId,
            CEPT.Level,
            CEPT.LatestAssessmentEndDate,
            CEPT.ProjectedAssessmentStartDate,
            CEPT.ActualAssessmentStartDate,
            CEPT.CategorizationOfNextAssessment,
            CEPT.IfEvmAssessmentDueWhatIsThePlanningStatus,
            CEPT.PlanningIsFundSecure,
            CEPT.PlanningleadCoAssessorSelected,
            CEPT.PlanningLeadAssessorName,
            CEPT.PlanningCoLeadAssessorName,
            CEPT.PlanningIsManagersTrained,
            CEPT.PlanningIsAssessorsTrained,
            CEPT.AssessmentIsDataCollectionCompleted,
            CEPT.AssessementEndDateCurrent,
            CEPT.AssessmentBestPracticesImplemented,
            CEPT.AssessmentIsQualityReviewEnded,
            CEPT.ReportWritingIsInitiated,
            CEPT.ReportWritingFinalized,
            CEPT.CipDevelopmentIsInitiated,
            CEPT.CipDevelopmentIsFinalized,
            CEPT.CreatedBy,
            CEPT.CreatedDate,
            CEPT.UpdatedBy,
            CEPT.UpdatedDate
          FROM SCIP.CountryEvmProcessTracker CEPT
          LEFT JOIN SCIP.CountryMaster CM ON CM.Id = CEPT.CountryId
          ORDER BY CEPT.LatestAssessmentEndDate DESC, CEPT.UpdatedDate DESC;
        `;
        const evmProcessData = await fetchScipQuery(evmProcessQuery, {});
        const nlwgQuery = `
          SELECT
            SNLWG.*,
            CM.Name AS CountryName,
            CM.IsoCode AS ISO3
          FROM SCIP.SnlwgPresenceAndFunctionality SNLWG
          LEFT JOIN SCIP.CountryMaster CM ON CM.Id = SNLWG.CountryId
          ORDER BY SNLWG.Year DESC, SNLWG.Quarter DESC, SNLWG.UpdatedDate DESC;
        `;
        const nlwgData = await fetchScipQuery(nlwgQuery, {});
        const genericDataMasterQuery = `
          SELECT
            GDM.Id,
            GDM.Code,
            GDM.NameJson,
            EN.Val AS NameEn
          FROM SCIP.GenericDataMaster GDM
          OUTER APPLY (
            SELECT TOP 1 v.[Value] AS Val
            FROM OPENJSON(GDM.NameJson)
            WITH ([Key] NVARCHAR(10) '$.Key', [Value] NVARCHAR(400) '$.Value') v
            WHERE v.[Key] = 'EN'
          ) EN
          WHERE ISNULL(GDM.IsDeleted, 0) = 0;
        `;
        const genericDataMasterData = await fetchScipQuery(genericDataMasterQuery, {});
        let gaviData = { data: [], columns: [] };
        let gaviError = '';
        let nisFinanceData = { data: [], columns: [] };
        let nisFinanceLoadError = '';

        try {
          const effectiveQuery = String(gaviFinanceDaxQuery || '').trim() || undefined;
          const effectiveTableName = effectiveQuery ? undefined : (String(gaviFinanceTableName || '').trim() || undefined);
          gaviData = await fetchPowerBISemanticData(
            gaviFinanceDatasetId,
            gaviFinanceWorkspaceId,
            effectiveQuery,
            5000,
            effectiveTableName
          );
        } catch (semanticError) {
          gaviError = semanticError?.message || 'Gavi 6.0 EVM Composite data is unavailable right now.';
        }

        try {
          nisFinanceData = await fetchPowerBIFabricSqlData({
            tableName: gaviFinanceUpdateTableName,
            maxRows: 5000
          });
        } catch (financeError) {
          nisFinanceLoadError = financeError?.message || 'Gavi finance update data is unavailable right now.';
        }

        if (!mounted) return;
        setCountries(countryRows);
        setScipRows(Array.isArray(scipData) ? scipData : []);
        setEvmProcessRows(Array.isArray(evmProcessData) ? evmProcessData : []);
        setNlwgRows(Array.isArray(nlwgData) ? nlwgData : []);
        setGenericDataMasterRows(Array.isArray(genericDataMasterData) ? genericDataMasterData : []);
        setGaviSemanticRows(Array.isArray(gaviData?.data) ? gaviData.data : []);
        setGaviSemanticColumns(Array.isArray(gaviData?.columns) ? gaviData.columns : []);
        setGaviSemanticError(gaviError);
        setNisFinanceRows(Array.isArray(nisFinanceData?.data) ? nisFinanceData.data : []);
        setNisFinanceColumns(Array.isArray(nisFinanceData?.columns) ? nisFinanceData.columns : []);
        setNisFinanceError(nisFinanceLoadError);
      } catch (loadError) {
        if (!mounted) return;
        setCountries([]);
        setScipRows([]);
        setEvmProcessRows([]);
        setNlwgRows([]);
        setGenericDataMasterRows([]);
        setGaviSemanticRows([]);
        setGaviSemanticColumns([]);
        setGaviSemanticError('');
        setNisFinanceRows([]);
        setNisFinanceColumns([]);
        setNisFinanceError('');
        setError(loadError?.message || 'Improvement planning data is unavailable right now.');
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    loadJourneyData();

    return () => {
      mounted = false;
    };
  }, [
    allowedIso3Values,
    gaviFinanceDatasetId,
    gaviFinanceWorkspaceId,
    gaviFinanceDaxQuery,
    gaviFinanceTableName,
    gaviFinanceUpdateTableName,
    normalizedAccessLevel
  ]);

  const gaviCountryColumnKey = useMemo(
    () => findColumnKey(gaviSemanticColumns, ['country']),
    [gaviSemanticColumns]
  );
  const gaviJourneyColumnKeys = useMemo(
    () => GAVI_JOURNEY_COLUMNS.map((column) => ({
      ...column,
      columnKey: findColumnKey(gaviSemanticColumns, column.names)
    })),
    [gaviSemanticColumns]
  );
  const nisFinanceCountryColumnKey = useMemo(
    () => findFinanceColumnKey(nisFinanceColumns, 'country'),
    [nisFinanceColumns]
  );
  const nisApplicationColumnKeys = useMemo(
    () => NIS_APPLICATION_COLUMNS.map((column) => ({
      ...column,
      columnKey: findFinanceColumnKey(nisFinanceColumns, column.match)
    })),
    [nisFinanceColumns]
  );
  const tableColumnCount = 7;
  const countryNameById = useMemo(() => {
    const map = new Map();
    countries.forEach((country) => {
      const id = String(country?.Id || '').trim();
      const name = String(country?.Name || '').trim();
      if (id && name) map.set(id, name);
    });
    return map;
  }, [countries]);
  const genericDataMasterMap = useMemo(() => {
    const map = new Map();
    genericDataMasterRows.forEach((row) => {
      const label = String(row?.NameEn || row?.Code || row?.Id || '').trim();
      if (!label) return;
      if (row?.Id !== null && row?.Id !== undefined) map.set(String(row.Id).trim(), label);
      if (row?.Code !== null && row?.Code !== undefined) map.set(String(row.Code).trim(), label);
    });
    return map;
  }, [genericDataMasterRows]);

  const journeyRows = useMemo(() => {
    const scipByCountry = new Map();
    const evmProcessByCountry = new Map();
    const nlwgByCountry = new Map();
    const gaviJourneyByCountry = new Map();

    scipRows.forEach((row) => {
      const iso3 = String(row?.ISO3 || row?.IsoCode || '').trim().toUpperCase();
      const countryName = String(row?.CountryName || row?.Country || '').trim().toLowerCase();
      const key = iso3 || countryName;
      if (!key) return;
      const current = scipByCountry.get(key) || [];
      current.push(row);
      scipByCountry.set(key, current);
    });

    evmProcessRows.forEach((row) => {
      const iso3 = String(row?.ISO3 || row?.IsoCode || '').trim().toUpperCase();
      const countryName = String(row?.CountryName || row?.Country || '').trim().toLowerCase();
      const key = iso3 || countryName;
      if (!key) return;
      if (!evmProcessByCountry.has(key)) {
        evmProcessByCountry.set(key, row);
      }
    });

    nlwgRows.forEach((row) => {
      const iso3 = String(row?.ISO3 || row?.IsoCode || '').trim().toUpperCase();
      const countryName = String(row?.CountryName || row?.Country || '').trim().toLowerCase();
      const key = iso3 || countryName;
      if (!key) return;
      if (!nlwgByCountry.has(key)) {
        nlwgByCountry.set(key, row);
      }
    });

    gaviSemanticRows.forEach((row) => {
      const country = normalizeCountryMatchValue(getRowText(row, gaviCountryColumnKey));
      if (!country) return;
      const values = {};
      gaviJourneyColumnKeys.forEach((column) => {
        values[column.key] = getRowText(row, column.columnKey);
      });
      gaviJourneyByCountry.set(country, { values, raw: row });
    });

    const nisFinanceByCountry = new Map();
    nisFinanceRows.forEach((row) => {
      const country = normalizeFinanceCountryName(getRowText(row, nisFinanceCountryColumnKey));
      if (!country || nisFinanceByCountry.has(country)) return;
      const nisValues = {};
      nisApplicationColumnKeys.forEach((column) => {
        nisValues[column.key] = formatNisFinanceValue(getRowText(row, column.columnKey), column.format);
      });
      nisFinanceByCountry.set(country, nisValues);
    });

    return countries
      .map((country) => {
        const iso3 = String(country?.IsoCode || country?.iso3 || country?.ISO3 || '').trim().toUpperCase();
        const countryName = String(country?.Name || country?.Country_Name || country?.country || iso3 || 'Unknown country').trim();
        const scipMatches = scipByCountry.get(iso3) || scipByCountry.get(countryName.toLowerCase()) || [];
        const latestScip = scipMatches[0] || null;
        const latestEvmProcess = evmProcessByCountry.get(iso3) || evmProcessByCountry.get(countryName.toLowerCase()) || null;
        const latestNlwg = nlwgByCountry.get(iso3) || nlwgByCountry.get(countryName.toLowerCase()) || null;
        const scipStatus = latestScip ? formatScipStatus(latestScip.Stage) : 'Not started';
        const gaviRecord = gaviJourneyByCountry.get(normalizeCountryMatchValue(countryName)) || {};
        const gaviJourney = gaviRecord.values || {};
        const financeCountry = normalizeFinanceCountryName(countryName);
        const nisApplication = nisFinanceByCountry.get(financeCountry) || {};

        return {
          key: iso3 || countryName,
          iso3,
          countryName,
          gaviJourney,
          nisApplication,
          hasNisFinance: nisFinanceByCountry.has(financeCountry),
          gaviRecord: gaviRecord.raw || null,
          latestEvmProcess,
          latestNlwg,
          scipCount: scipMatches.length,
          scipStatus,
          scipStatusClass: getScipStatusClass(scipStatus),
          latestScip
        };
      })
      .filter((row) => String(row.gaviJourney.evmComposite || '').trim());
  }, [
    countries,
    evmProcessRows,
    gaviCountryColumnKey,
    gaviJourneyColumnKeys,
    gaviSemanticRows,
    nisApplicationColumnKeys,
    nisFinanceCountryColumnKey,
    nisFinanceRows,
    nlwgRows,
    scipRows
  ]);

  useEffect(() => {
    if (journeyRows.length === 0) {
      if (selectedCountryKey) setSelectedCountryKey('');
      return;
    }

    if (!journeyRows.some((row) => row.key === selectedCountryKey)) {
      setSelectedCountryKey(journeyRows[0].key);
    }
  }, [journeyRows, selectedCountryKey]);

  const selectedJourneyRow = useMemo(
    () => journeyRows.find((row) => row.key === selectedCountryKey) || null,
    [journeyRows, selectedCountryKey]
  );
  const displayedJourneyRows = showAllJourneyCountries ? journeyRows : journeyRows.slice(0, 8);

  useEffect(() => {
    setShowAllNlwgFields(false);
  }, [selectedCountryKey, activeSnapshotTab]);

  const selectedEvmaDetails = useMemo(() => {
    if (!selectedJourneyRow?.latestEvmProcess) return [];
    return Object.entries(selectedJourneyRow.latestEvmProcess)
      .filter(([key]) => !['CountryName', 'ISO3'].includes(key))
      .map(([key, value]) => ({
        key,
        label: key === 'CountryId' ? 'Country' : formatFieldLabel(key),
        value: key === 'CountryId'
          ? countryNameById.get(String(value || '').trim()) || formatFieldValue(key, value, genericDataMasterMap)
          : formatFieldValue(key, value, genericDataMasterMap)
      }));
  }, [countryNameById, genericDataMasterMap, selectedJourneyRow]);

  const selectedCipDetails = useMemo(() => {
    if (!selectedJourneyRow?.latestScip) return [];
    return Object.entries(selectedJourneyRow.latestScip)
      .map(([key, value]) => ({
        key,
        label: formatFieldLabel(key),
        value: formatFieldValue(key, value)
      }));
  }, [selectedJourneyRow]);

  const selectedNlwgPrimaryDetails = useMemo(() => {
    if (!selectedJourneyRow?.latestNlwg) return [];
    return NLWG_PRIMARY_FIELDS.map((field) => ({
      key: field.key,
      label: field.label,
      value: formatFieldValue(field.key, selectedJourneyRow.latestNlwg[field.key], genericDataMasterMap)
    }));
  }, [genericDataMasterMap, selectedJourneyRow]);

  const selectedNlwgDetails = useMemo(() => {
    if (!selectedJourneyRow?.latestNlwg) return [];
    const primaryKeys = new Set(NLWG_PRIMARY_FIELDS.map((field) => field.key));
    return Object.entries(selectedJourneyRow.latestNlwg)
      .filter(([key]) => !['CountryName', 'ISO3'].includes(key))
      .filter(([key]) => !primaryKeys.has(key))
      .map(([key, value]) => ({
        key,
        label: key === 'CountryId' ? 'Country' : formatFieldLabel(key),
        value: key === 'CountryId'
          ? countryNameById.get(String(value || '').trim()) || formatFieldValue(key, value, genericDataMasterMap)
          : formatFieldValue(key, value, genericDataMasterMap)
      }));
  }, [countryNameById, genericDataMasterMap, selectedJourneyRow]);

  const selectedNisDetails = useMemo(() => {
    if (!selectedJourneyRow?.hasNisFinance) return [];
    return NIS_APPLICATION_COLUMNS
      .filter((field) => field.key !== 'period' && field.key !== 'reprogrammingPeriod')
      .map((field) => ({
        key: field.key,
        label: field.label,
        value: selectedJourneyRow.nisApplication[field.key] || 'N/A'
      }));
  }, [selectedJourneyRow]);

  return (
    <section className="home-section vaccines-page-section improvement-journey-section">
      <div className="vaccines-dashboard-header">
        <div>
          <p className="vaccines-section-label">Country Improvement Journey</p>
          <h2 className="vaccines-dashboard-title">Improvement Planning</h2>
        </div>
      </div>

      <div className="vaccines-schedule-card vaccines-matrix-card">
        <div className="vaccines-card-header">
          <div>
            <p className="vaccines-section-label">Process x Country Matrix</p>
            <h3 className="vaccines-card-title">Country progress by improvement process</h3>
          </div>
        </div>

        <div className="vaccines-matrix-wrap">
          <table className="vaccines-matrix-table improvement-journey-table" aria-label="Improvement planning journey by country">
            <thead>
              <tr>
                <th scope="col">Country</th>
                <th scope="col">NIS Period</th>
                <th scope="col">GAVI 6.0</th>
                <th scope="col">NIS Budget</th>
                <th scope="col">EVM Score</th>
                <th scope="col">EVMA</th>
                <th scope="col">SCIP Status</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={tableColumnCount}>Loading improvement planning journey...</td>
                </tr>
              )}
              {!isLoading && error && (
                <tr>
                  <td colSpan={tableColumnCount}>{error}</td>
                </tr>
              )}
              {!isLoading && !error && journeyRows.length === 0 && (
                <tr>
                  <td colSpan={tableColumnCount}>No countries with EVM Composite values found for this view.</td>
                </tr>
              )}
              {!isLoading && !error && displayedJourneyRows.map((row) => (
                <tr
                  key={row.key}
                  className={row.key === selectedCountryKey ? 'is-selected' : ''}
                  aria-selected={row.key === selectedCountryKey}
                >
                  <th scope="row">
                    <button type="button" onClick={() => setSelectedCountryKey(row.key)}>
                      {row.countryName}
                    </button>
                    {row.iso3 && <small>{row.iso3}</small>}
                  </th>
                  <td>
                    {(row.nisApplication.period || row.nisApplication.reprogrammingPeriod) ? (
                      <button
                        type="button"
                        className="journey-status-pill is-dashboard"
                        onClick={() => {
                          setSelectedCountryKey(row.key);
                          setActiveSnapshotTab('nis');
                        }}
                      >
                        {row.nisApplication.period || row.nisApplication.reprogrammingPeriod}
                      </button>
                    ) : (
                      <span className="journey-status-pill is-empty">No NIS data</span>
                    )}
                  </td>
                  <td>
                    {row.nisApplication.applicationType || row.nisApplication.submissionToGavi || row.nisApplication.reprogrammingSubmission ? (
                      <button
                        type="button"
                        className="journey-main-cell journey-table-button"
                        onClick={() => {
                          setSelectedCountryKey(row.key);
                          setActiveSnapshotTab('nis');
                        }}
                      >
                        <span className="journey-application-type">{row.nisApplication.applicationType || 'N/A'}</span>
                        <span className="journey-deadline-text">
                          <Clock size={13} aria-hidden="true" />
                          <span>
                            {row.nisApplication.submissionToGavi || row.nisApplication.reprogrammingSubmission
                              ? formatDate(row.nisApplication.submissionToGavi || row.nisApplication.reprogrammingSubmission)
                              : 'N/A'}
                          </span>
                        </span>
                      </button>
                    ) : (
                      <span className="journey-empty-text">No NIS data</span>
                    )}
                  </td>
                  <td>
                    {row.nisApplication.guaranteedVaccineBudget || row.nisApplication.coFinancingObligations ? (
                      <button
                        type="button"
                        className="journey-main-cell journey-table-button"
                        onClick={() => {
                          setSelectedCountryKey(row.key);
                          setActiveSnapshotTab('nis');
                        }}
                      >
                        <span className="journey-application-type">
                          Budget: {row.nisApplication.guaranteedVaccineBudget || 'N/A'}
                        </span>
                        <small>
                          Co-financing: {row.nisApplication.coFinancingObligations || 'N/A'}
                        </small>
                      </button>
                    ) : (
                      <span className="journey-empty-text">No NIS data</span>
                    )}
                  </td>
                  <td>
                    <div className="journey-score-cell">
                      <strong>{formatGaviValue('evmComposite', row.gaviJourney.evmComposite)}</strong>
                    </div>
                  </td>
                  <td>
                    {row.latestEvmProcess ? (
                      <div className="journey-main-cell">
                        <span className="journey-status-pill is-dashboard">
                          {formatFieldValue(
                            'IfEvmAssessmentDueWhatIsThePlanningStatus',
                            row.latestEvmProcess.IfEvmAssessmentDueWhatIsThePlanningStatus,
                            genericDataMasterMap
                          )}
                        </span>
                        {row.latestEvmProcess.ProjectedAssessmentStartDate && (
                          <small>Next EVMA: {formatDate(row.latestEvmProcess.ProjectedAssessmentStartDate)}</small>
                        )}
                      </div>
                    ) : (
                      <span className="journey-status-pill is-empty">No EVMA data</span>
                    )}
                  </td>
                  <td>
                    {row.latestScip ? (
                      <button
                        type="button"
                        className={`journey-status-pill ${row.scipStatusClass}`}
                        onClick={() => {
                          setSelectedCountryKey(row.key);
                          setActiveSnapshotTab('cip');
                        }}
                      >
                        {row.scipStatus}
                      </button>
                    ) : (
                      <span className="journey-status-pill is-empty">Not started</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {journeyRows.length > 8 && (
          <div className="vaccines-matrix-footer">
            <p className="vaccines-footnote">
              Showing {displayedJourneyRows.length} of {journeyRows.length} countries in this view.
            </p>
            <button
              type="button"
              className="vaccines-expand-button"
              onClick={() => setShowAllJourneyCountries((current) => !current)}
            >
              {showAllJourneyCountries ? 'Show fewer countries' : `Show all ${journeyRows.length} countries`}
            </button>
          </div>
        )}
      </div>

      <div className="vaccines-schedule-card budgets-snapshot-card improvement-journey-snapshot-card">
        <div className="vaccines-card-header">
          <div>
            <p className="vaccines-section-label">Country Snapshot</p>
          </div>
        </div>

        {isLoading && <p className="budgets-data-note">Loading country snapshot...</p>}
        {!isLoading && !selectedJourneyRow && (
          <p className="budgets-data-note">Click a country in the table above to view country details.</p>
        )}

        {!isLoading && selectedJourneyRow && (
          <>
            <div className="budgets-country-detail-tables">
              <div className="vaccines-card-header budgets-gavi-header">
                <div className="journey-snapshot-tabs" role="tablist" aria-label="Country snapshot detail tabs">
                  {SNAPSHOT_TABS.map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      role="tab"
                      aria-selected={activeSnapshotTab === tab.key}
                      className={activeSnapshotTab === tab.key ? 'is-active' : ''}
                      onClick={() => setActiveSnapshotTab(tab.key)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="vaccines-wuenic-table-wrap budgets-table-wrap budgets-gavi-country-wrap">
                <table className="vaccines-wuenic-table budgets-gavi-country-detail-table" aria-label="Selected country improvement planning details">
                  <thead>
                    <tr>
                      <th scope="col">Field</th>
                      <th scope="col">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeSnapshotTab === 'evma' && selectedEvmaDetails.length === 0 && (
                      <tr>
                        <td colSpan="2">No EVMA tracker details found for this country.</td>
                      </tr>
                    )}
                    {activeSnapshotTab === 'evma' && selectedEvmaDetails.map((field) => (
                      <tr key={`evma-detail-${selectedJourneyRow.key}-${field.key}`}>
                        <th scope="row">{field.label}</th>
                        <td>{field.value}</td>
                      </tr>
                    ))}
                    {activeSnapshotTab === 'cip' && selectedCipDetails.length === 0 && (
                      <tr>
                        <td colSpan="2">No CIP details found for this country.</td>
                      </tr>
                    )}
                    {activeSnapshotTab === 'cip' && selectedCipDetails.map((field) => (
                      <tr key={`cip-detail-${selectedJourneyRow.key}-${field.key}`}>
                        <th scope="row">{field.label}</th>
                        <td>{field.value}</td>
                      </tr>
                    ))}
                    {activeSnapshotTab === 'nis' && selectedNisDetails.length === 0 && (
                      <tr>
                        <td colSpan="2">No NIS GAVI 6.0 application details found for this country.</td>
                      </tr>
                    )}
                    {activeSnapshotTab === 'nis' && selectedNisDetails.map((field) => (
                      <tr key={`nis-detail-${selectedJourneyRow.key}-${field.key}`}>
                        <th scope="row">{field.label}</th>
                        <td>{field.value}</td>
                      </tr>
                    ))}
                    {activeSnapshotTab === 'nlwg' && selectedNlwgPrimaryDetails.length === 0 && (
                      <tr>
                        <td colSpan="2">No NLWG details found for this country.</td>
                      </tr>
                    )}
                    {activeSnapshotTab === 'nlwg' && selectedNlwgPrimaryDetails.map((field) => (
                      <tr key={`nlwg-detail-${selectedJourneyRow.key}-${field.key}`}>
                        <th scope="row">{field.label}</th>
                        <td>{field.value}</td>
                      </tr>
                    ))}
                    {activeSnapshotTab === 'nlwg' && selectedNlwgDetails.length > 0 && (
                      <tr>
                        <td colSpan="2">
                          <button
                            type="button"
                            className="budgets-ta-read-more-button"
                            onClick={() => setShowAllNlwgFields((current) => !current)}
                          >
                            {showAllNlwgFields ? 'Hide additional NLWG fields' : `Show all NLWG fields (${selectedNlwgDetails.length})`}
                          </button>
                        </td>
                      </tr>
                    )}
                    {activeSnapshotTab === 'nlwg' && showAllNlwgFields && selectedNlwgDetails.map((field) => (
                      <tr key={`nlwg-extra-detail-${selectedJourneyRow.key}-${field.key}`}>
                        <th scope="row">{field.label}</th>
                        <td>{field.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="improvement-journey-note">
        <ClipboardList size={16} />
        <span>
          Strategic planning and EVM values come from the Gavi 6.0 dataset. NIS budget figures come from the Gavi finance update.
          {gaviSemanticError ? ` ${gaviSemanticError}` : ''}
          {nisFinanceError ? ` ${nisFinanceError}` : ''}
        </span>
      </div>
    </section>
  );
};

export default ImprovementPlanningJourney;
